// library imports
import { useState, useEffect, useContext } from "react";
import {
  Row,
  Select,
  Button,
  Col,
  Flex,
  DatePicker,
  Tooltip,
  Collapse,
  Grid,
  Form,
  Modal,
  Upload,
  message,
} from "antd";
import { FileAddOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

// hook imports
import useFetch from "../../hooks/useFetch";

// context imports
import { IndexContext } from "../../context/IndexContext";

// component imports
import CustomEditTable from "../../components/common/CustomEditTable";
import ActivityDataForm from "../../components/emission_data_entry/ActivityDataForm";
import FactorValueMarketBasedSelection from "../../components/emission_data_entry/FactorValueMarketBasedSelection";
import ActivityDataRemark from "../../components/emission_data_entry/ActivityDataRemark";
import ActivityDataSource from "../../components/emission_data_entry/ActivityDataSource";

// constant imports
import { VERIFIER_ROLE_ID } from "../../configs/configs";

const { useBreakpoint } = Grid;

export default function EmissionDataEntry() {
  const screens = useBreakpoint();

  const { notificationApi, messageApi, siteSelected, authenticationState } =
    useContext(IndexContext);
  const [fetchWrapper] = useFetch();

  const [loading, setLoading] = useState(false);
  const [emissionSourceSelectionLoading, setEmissionSourceSelectionLoading] =
    useState(false);
  const [dimensionSelectionLoading, setDimensionSelectionLoading] =
    useState(false);

  // selected month year
  const [selectedMonthYear, setSelectedMonthYear] = useState(
    dayjs().endOf("M"),
  );

  // custom edit table related props
  const [form] = Form.useForm(); // form instance
  const [data, setData] = useState([]); // data for the emission table
  const [emissionSourceSelection, setEmissionSourceSelection] = useState([]); // options for emission source
  const [dimensionSelection, setDimensionSelection] = useState([]); // options for dimension
  const [editClicked, setEditClicked] = useState(false); // this is used to disabled 'Add ...' button when the record is being edited as the data / selection in the modal won't be correct, so disable editing of modal data when the row of record is being edited
  const [fileList, setFileList] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);

  // collapse items related props
  const [parentActiveKey, setParentActiveKey] = useState([]); // this key is used to select correct file for emission source and dimension selection
  const [childActiveKey, setChildActiveKey] = useState([]); // this key is used to select correct file for emission source and dimension selection
  const [grandchildActiveKey, setGrandchildActiveKey] = useState([]);

  // modal related props
  const [currentlyEditEmissionID, setCurrentlyEditEmissionID] = useState(null);
  const [isActivityDataModalOpen, setIsActivityDataModalOpen] = useState(false);
  const [
    isFactorValueMarketBasedModalOpen,
    setIsFactorValueMarketBasedModalOpen,
  ] = useState(false);
  const [isActivityDataRemarkModalOpen, setIsActivityDataRemarkModalOpen] =
    useState(false);
  const [isActivityDataSourceModalOpen, setIsActivityDataSourceModalOpen] =
    useState(false);

  const [existingActivityData, setExistingActivityData] = useState(null);
  const [
    existingFactorValueMarketBasedData,
    setExistingFactorValueMarketBasedData,
  ] = useState(null);
  const [
    existingFactorValueEmissionFactorIDList,
    setExistingFactorValueEmissionFactorIDList,
  ] = useState(null);
  const [existingActivityDataRemark, setExistingActivityDataRemark] =
    useState(null);
  const [existingActivityDataSource, setExistingActivityDataSource] =
    useState(null);

  const beforeUpload = (file) => {
    const isLimit20MB = file.size / 1024 / 1024 < 20;
    if (!isLimit20MB) {
      message.error("File size must be smaller than 20MB!");
    }
    return isLimit20MB;
  };

  // emission table columns
  const columns = [
    {
      title: "Emission Source",
      dataIndex: "EmissionSource",
      inputType: "select",
      width: "12.5%",
      editable: true,
      required: true,
      fieldComponent: (
        <Select
          options={emissionSourceSelection}
          disabled={emissionSourceSelection.length === 0}
          loading={emissionSourceSelectionLoading}
          showSearch
        />
      ),
    },
    {
      title: "Dimension",
      dataIndex: "Dimension",
      inputType: "select",
      width: "12.5%",
      editable: true,
      required: childActiveKey[0] === "stationary_combustion" ? true : false,
      fieldComponent: (
        <Select
          options={dimensionSelection}
          disabled={dimensionSelection.length === 0}
          loading={dimensionSelectionLoading}
        />
      ),
    },
    {
      title: "Activity Data",
      dataIndex: "ActivityData",
      inputType: "input",
      width: "12.5%",
      render: (_, record) => {
        return (
          <Flex align="center">
            <p className="mr-2">{record.ActivityData}</p>
            <Button
              disabled={
                !record.ActivityData ||
                editClicked === true ||
                authenticationState?.user_type === VERIFIER_ROLE_ID
              }
              icon={<FileAddOutlined />}
              type="text"
              onClick={() => {
                setCurrentlyEditEmissionID(record.ID);
                setIsActivityDataModalOpen(true);

                if (record.ActivityData !== "--") {
                  setExistingActivityData(record.ActivityData);
                } else {
                  setExistingActivityData(null);
                }
              }}
            />
          </Flex>
        );
      },
    },
    {
      // title: "Factor Value (Market-based)",
      title: "Factor Value",
      dataIndex: "FactorValueAndUnit",
      inputType: "button",
      width: "12.5%",
      render: (_, record) => {
        return (
          <Flex align="center">
            <p className="mr-2">{record.FactorValueAndUnit}</p>
            <Button
              disabled={
                !record.FactorValueAndUnit ||
                editClicked === true ||
                authenticationState?.user_type === VERIFIER_ROLE_ID
              }
              icon={<FileAddOutlined />}
              type="text"
              onClick={() => {
                setCurrentlyEditEmissionID(record.ID);
                setIsFactorValueMarketBasedModalOpen(true);

                if (record.FactorValueAndUnit !== "--") {
                  setExistingFactorValueMarketBasedData(
                    record.FactorValueAndUnit,
                  );
                  setExistingFactorValueEmissionFactorIDList(
                    record.EmissionFactorIDList,
                  );
                } else {
                  setExistingFactorValueMarketBasedData(null);
                  setExistingFactorValueEmissionFactorIDList(null);
                }
              }}
            />
          </Flex>
        );
      },
    },
    {
      title: "Carbon Emission",
      dataIndex: "CarbonEmission",
      inputType: "input",
      width: "8%",
    },
    {
      title: "Activity Data Source",
      dataIndex: "ActivityDataSource",
      inputType: "input",
      width: "8%",
      render: (_, record) => {
        return (
          <Flex align="center">
            <p className="mr-2">{record.ActivityDataSource}</p>
            <Button
              disabled={
                !record.Remark ||
                authenticationState?.user_type === VERIFIER_ROLE_ID
              }
              icon={<FileAddOutlined />}
              type="text"
              onClick={() => {
                setCurrentlyEditEmissionID(record.ID);
                setIsActivityDataSourceModalOpen(true);

                if (record.ActivityDataSource !== "--") {
                  setExistingActivityDataSource(record.ActivityDataSource);
                } else {
                  setExistingActivityDataSource(null);
                }
              }}
            />
          </Flex>
        );
      },
    },
    {
      title: "Remark",
      dataIndex: "Remark",
      inputType: "input",
      width: "8%",
      render: (_, record) => {
        return (
          <Flex align="center">
            <p className="mr-2">{record.Remark}</p>
            <Button
              disabled={
                !record.Remark ||
                authenticationState?.user_type === VERIFIER_ROLE_ID
              }
              icon={<FileAddOutlined />}
              type="text"
              onClick={() => {
                setCurrentlyEditEmissionID(record.ID);
                setIsActivityDataRemarkModalOpen(true);

                if (record.Remark !== "--") {
                  setExistingActivityDataRemark(record.Remark);
                } else {
                  setExistingActivityDataRemark(null);
                }
              }}
            />
          </Flex>
        );
      },
    },
    // {
    //   title: "Uncertainty",
    //   dataIndex: "Uncertainty",
    //   inputType: "button",
    //   width: "240px",
    //   render: (_, record) => {
    //     return (
    //       <Flex align="center">
    //         <p className="mr-2">{record.Uncertainty}</p>

    //         <Tooltip title="Add Uncertainty">
    //           <Button
    //             disabled={!record.Uncertainty  || editClicked === true}
    //             icon={<FileAddOutlined />}
    //             type="text"
    //             onClick={() => {}}
    //           />
    //         </Tooltip>
    //       </Flex>
    //     );
    //   },
    // },
    {
      title: "Upload",
      dataIndex: "upload",
      width: "12.5%",
      render: (_, record) => {
        return (
          <Flex gap="middle" wrap>
            <Upload
              beforeUpload={beforeUpload}
              fileList={fileList.filter(
                (f) => f.activity_data_ID === record.ID,
              )}
              customRequest={(options) => handleUpload(options, record.ID)}
              onRemove={
                authenticationState?.user_type === VERIFIER_ROLE_ID
                  ? false
                  : deleteFile
              }
            >
              <Button
                type="primary"
                disabled={
                  uploadLoading ||
                  !record.ID ||
                  authenticationState?.user_type === VERIFIER_ROLE_ID
                }
                style={{ width: "100px" }}
              >
                Upload
              </Button>
            </Upload>
          </Flex>
        );
      },
    },
  ];

  // scope 1 nested items
  const scope1NestedItems = [
    {
      key: "stationary_combustion",
      label: "Stationary Combustion",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          // handleDataType="scope_1_stationary_combustion"
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "mobile_combustion_fuel_use",
      label: "Mobile Combustion - Fuel Use",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "mobile_combustion_distance",
      label: "Mobile Combustion - Distance",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "refrigerant",
      label: "Refrigerant",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "process",
      label: "Process",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
  ];

  // scope 2 nested items
  const scope2NestedItems = [
    {
      key: "electricity",
      label: (
        <Flex align="center">
          <div>Electricity</div>
          <Tooltip
            title="
            Disclaimer:
            The Scope 2 electricity emission factor used in this report is applicable only to Malaysia's national grid. 
            Calculations performed outside Malaysia may not be accurate and should not rely on this factor. 
            Users are advised to refer to their local grid emission factors or approved national datasets for accurate reporting."
          >
            <Button type="text" icon={<InfoCircleOutlined />} />
          </Tooltip>
        </Flex>
      ),
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
  ];

  const scope3TransportItems = [
    {
      key: "mobile_combustion_fuel_use",
      label: "Mobile Combustion - Fuel Use",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "mobile_combustion_distance",
      label: "Mobile Combustion - Distance",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "mobile_combustion_freight",
      label: "Mobile Combustion - Freight",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "mobile_combustion_public",
      label: "Mobile Combustion - Public",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
  ];

  // scope 3 nested items
  const scope3NestedItems = [
    {
      key: "purchased_goods_services",
      label: "Purchased Goods and Services (Upstream)",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "capital_goods",
      label: "Capital Goods (Upstream)",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "fuel_energy_related_activities",
      label:
        "Fuel- and energy-related activities (not included in Scope 1 or 2) (Upstream)",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "inbound_transport_goods_sold",
      label: "Transportation and distribution (Upstream)",
      children: (
        <Collapse
          accordion
          items={scope3TransportItems}
          onChange={(key) => setGrandchildActiveKey(key)}
          destroyInactivePanel={true}
        />
      ),
    },
    {
      key: "waste",
      label: "Waste Generated in Operations (Upstream)",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "business_travel",
      label: "Business Travel (Upstream)",
      children: (
        <Collapse
          accordion
          items={scope3TransportItems}
          onChange={(key) => setGrandchildActiveKey(key)}
          destroyInactivePanel={true}
        />
      ),
    },
    {
      key: "employee_commuting",
      label: "Employee Commuting (Upstream)",
      children: (
        <Collapse
          accordion
          items={scope3TransportItems}
          onChange={(key) => setGrandchildActiveKey(key)}
          destroyInactivePanel={true}
        />
      ),
    },
    {
      key: "upstream_leased_assets",
      label: "Leased Assets (Upstream)",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "outbound_transport_purchased_goods",
      label: "Transportation and distribution (Downstream)",
      children: (
        <Collapse
          accordion
          items={scope3TransportItems}
          onChange={(key) => setGrandchildActiveKey(key)}
          destroyInactivePanel={true}
        />
      ),
    },
    {
      key: "processing_sold_products",
      label: "Processing of Sold Products (Downstream)",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "use_sold_products",
      label: "Use of Sold Products (Downstream)",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "end_of_life_sold_products",
      label: "End-of-life Treatment of Sold Products (Downstream)",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "downstream_leased_assets",
      label: "Leased Assets (Downstream)",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "franchises",
      label: "Franchises (Downstream)",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
    {
      key: "investments",
      label: "Investments (Downstream)",
      children: (
        <CustomEditTable
          name={"Emission"}
          form={form}
          columnType={columns}
          data={data}
          setData={setData}
          deleteFunction={true}
          addFunction={true}
          calculateFunction={true}
          getDimensionSelection={getDimensionSelection}
          saveData={saveEmissionData}
          updateData={updateEmissionData}
          deleteData={deleteEmissionData}
          calculateData={calculateEmissionData}
          setEditClicked={setEditClicked}
          handleUpload={handleUpload}
          uploadLoading={uploadLoading}
          fileList={fileList}
          deleteFile={deleteFile}
          addButtonText="Add Emission"
          loading={loading}
          addDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
          deleteDisabled={authenticationState?.user_type === VERIFIER_ROLE_ID}
        />
      ),
    },
  ];

  // parent scope items
  const scopeItems = [
    {
      key: "scope_1",
      label: (
        <Row>
          <Col xs={24} sm={24} md={16}>
            {screens.md ? "Scope 1: Direct GHG Emission" : "Scope 1"}
          </Col>
          {/* <Col
            xs={24}
            sm={24}
            md={8}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "baseline",
            }}
          >
            Total : {Number(emissionTotal).toFixed(2)} tCO<sub>2</sub>e
          </Col> */}
        </Row>
      ),
      children: (
        <Collapse
          accordion
          // defaultActiveKey={["stationary_combution"]}
          items={scope1NestedItems}
          onChange={(key) => setChildActiveKey(key)}
          destroyInactivePanel={true}
        />
      ),
    },
    {
      key: "scope_2",
      label: (
        <Row>
          <Col xs={24} sm={24} md={16}>
            {screens.md
              ? "Scope 2: Electricity Indirect GHG Emission"
              : "Scope 2"}
          </Col>
        </Row>
      ),
      children: (
        <Collapse
          accordion
          items={scope2NestedItems}
          onChange={(key) => setChildActiveKey(key)}
          destroyInactivePanel={true}
        />
      ),
    },
    {
      key: "scope_3",
      label: (
        <Row>
          <Col xs={24} sm={24} md={16}>
            {screens.md ? "Scope 3: Other Indirect GHG Emission" : "Scope 3"}
          </Col>
        </Row>
      ),
      children: (
        <Collapse
          accordion
          items={scope3NestedItems}
          onChange={(key) => setChildActiveKey(key)}
          destroyInactivePanel={true}
        />
      ),
    },
  ];

  /** API to get all emission data */
  function getAllEmissionData() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "emission_data_entry/getAllEmissionData",
      method: "GET",
      params: {
        month_year: selectedMonthYear.endOf("M").format("YYYY-MM-DD"),
        site_ID: siteSelected,
        parent_active_key: parentActiveKey[0],
        child_active_key: childActiveKey[0],
        grandchild_active_key:
          parentActiveKey[0] === "scope_3"
            ? childActiveKey[0] !== "purchased_goods_services" &&
              childActiveKey[0] !== "waste" &&
              childActiveKey[0] !== "capital_goods" &&
              childActiveKey[0] !== "fuel_energy_related_activities" &&
              childActiveKey[0] !== "upstream_leased_assets" &&
              childActiveKey[0] !== "use_sold_products" &&
              childActiveKey[0] !== "end_of_life_sold_products" &&
              childActiveKey[0] !== "downstream_leased_assets" &&
              childActiveKey[0] !== "franchises" &&
              childActiveKey[0] !== "investments" &&
              childActiveKey[0] !== "processing_sold_products"
              ? grandchildActiveKey[0]
              : null
            : null,
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        setData(response.data);
        setLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setLoading(false);
      },
    });
  }

  /** API to get emission source selection */
  function getEmissionSourceSelection() {
    setEmissionSourceSelectionLoading(true);
    fetchWrapper({
      endpoint_url: "emission_data_entry/getEmissionSourceSelection",
      method: "GET",
      params: {
        scope: parentActiveKey[0],
        scope_type: childActiveKey[0],
        category_type:
          parentActiveKey[0] === "scope_3"
            ? childActiveKey[0] !== "purchased_goods_services" &&
              childActiveKey[0] !== "waste" &&
              childActiveKey[0] !== "capital_goods" &&
              childActiveKey[0] !== "fuel_energy_related_activities" &&
              childActiveKey[0] !== "upstream_leased_assets" &&
              childActiveKey[0] !== "use_sold_products" &&
              childActiveKey[0] !== "end_of_life_sold_products" &&
              childActiveKey[0] !== "downstream_leased_assets" &&
              childActiveKey[0] !== "franchises" &&
              childActiveKey[0] !== "investments" &&
              childActiveKey[0] !== "processing_sold_products"
              ? grandchildActiveKey[0]
              : null
            : null,
      },
      set_content_type_header: false,
      onSuccess: (response) => {
        if (response.data.length === 0) {
          notificationApi.info({
            message: response.message,
          });
          setEmissionSourceSelectionLoading(false);
        }
        setEmissionSourceSelection(response.data);
        setEmissionSourceSelectionLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setEmissionSourceSelectionLoading(false);
      },
    });
  }

  /** API to get dimension selection */
  function getDimensionSelection(emissionSourceSelected) {
    if (emissionSourceSelected) {
      setDimensionSelectionLoading(true);
      fetchWrapper({
        endpoint_url: "emission_data_entry/getDimensionSelection",
        method: "GET",
        params: {
          emission_source_selected: emissionSourceSelected,
          scope: parentActiveKey[0],
          scope_type: childActiveKey[0],
          category_type:
            parentActiveKey[0] === "scope_3"
              ? childActiveKey[0] !== "purchased_goods_services" &&
                childActiveKey[0] !== "waste" &&
                childActiveKey[0] !== "capital_goods" &&
                childActiveKey[0] !== "fuel_energy_related_activities" &&
                childActiveKey[0] !== "upstream_leased_assets" &&
                childActiveKey[0] !== "use_sold_products" &&
                childActiveKey[0] !== "end_of_life_sold_products" &&
                childActiveKey[0] !== "downstream_leased_assets" &&
                childActiveKey[0] !== "franchises" &&
                childActiveKey[0] !== "investments" &&
                childActiveKey[0] !== "processing_sold_products"
                ? grandchildActiveKey[0]
                : null
              : null,
        },
        set_content_type_header: false,
        onSuccess: (response) => {
          setDimensionSelection(response.data);
          setDimensionSelectionLoading(false);
        },
        onResponseError: (response) => {
          notificationApi.error({
            message: response.message,
          });
          setDimensionSelectionLoading(false);
        },
      });
    }
  }

  /** API to save data to backend */
  function saveEmissionData(newData) {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "emission_data_entry/saveEmissionData",
      method: "POST",
      body: {
        emission_source: newData["EmissionSource"],
        dimension: newData["Dimension"],
        site_ID: siteSelected,
        month_year: selectedMonthYear.endOf("M").format("YYYY-MM-DD"),
        scope: parentActiveKey[0],
        scope_type: childActiveKey[0],
        category_type:
          parentActiveKey[0] === "scope_3"
            ? childActiveKey[0] !== "purchased_goods_services" &&
              childActiveKey[0] !== "waste" &&
              childActiveKey[0] !== "capital_goods" &&
              childActiveKey[0] !== "fuel_energy_related_activities" &&
              childActiveKey[0] !== "upstream_leased_assets" &&
              childActiveKey[0] !== "use_sold_products" &&
              childActiveKey[0] !== "end_of_life_sold_products" &&
              childActiveKey[0] !== "downstream_leased_assets" &&
              childActiveKey[0] !== "franchises" &&
              childActiveKey[0] !== "investments" &&
              childActiveKey[0] !== "processing_sold_products"
              ? grandchildActiveKey[0]
              : null
            : null,
      },
      onSuccess: (response) => {
        notificationApi.success({
          message: response.message,
        });
        getAllEmissionData();
        setLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setLoading(false);
      },
    });
  }

  /** API to save edited emission data */
  function updateEmissionData(emission_ID_to_be_edited, editedData) {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "emission_data_entry/updateEmissionData",
      method: "POST",
      body: {
        data: editedData,
        emission_ID: emission_ID_to_be_edited,
      },
      onSuccess: (response) => {
        notificationApi.success({
          message: response.message,
        });
        getAllEmissionData();
        setLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
      },
    });
  }

  /** API to delete emission data */
  function deleteEmissionData(activity_data_ID_to_be_deleted) {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "emission_data_entry/deleteEmissionData",
      method: "POST",
      body: {
        activity_data_ID: activity_data_ID_to_be_deleted,
      },
      onSuccess: (response) => {
        notificationApi.success({
          message: response.message,
        });
        getAllEmissionData();
        setLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setLoading(false);
      },
    });
  }

  /** API to calculate all emission data */
  function calculateEmissionData() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "emission_data_entry/calculateEmissionData",
      method: "POST",
      body: {
        month_year: selectedMonthYear.endOf("M").format("YYYY-MM-DD"),
        site_ID: siteSelected,
        parent_active_key: parentActiveKey[0],
        child_active_key: childActiveKey[0],
        grandchild_active_key:
          parentActiveKey[0] === "scope_3"
            ? childActiveKey[0] !== "purchased_goods_services" &&
              childActiveKey[0] !== "waste" &&
              childActiveKey[0] !== "capital_goods" &&
              childActiveKey[0] !== "fuel_energy_related_activities" &&
              childActiveKey[0] !== "upstream_leased_assets" &&
              childActiveKey[0] !== "use_sold_products" &&
              childActiveKey[0] !== "end_of_life_sold_products" &&
              childActiveKey[0] !== "downstream_leased_assets" &&
              childActiveKey[0] !== "franchises" &&
              childActiveKey[0] !== "investments" &&
              childActiveKey[0] !== "processing_sold_products"
              ? grandchildActiveKey[0]
              : null
            : null,
      },
      onSuccess: (response) => {
        if (response.data.status === true) {
          notificationApi.success({
            message: response.message,
          });
        } else {
          notificationApi.error({
            message: response.message,
          });
        }
        getAllEmissionData();
        setLoading(false);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: response.message,
        });
        setLoading(false);
      },
    });
  }

  //handle upload
  function handleUpload(upload_detail, uploadActivityDataID) {
    // indicate uploading is performing
    setUploadLoading(true);

    const formData = new FormData();
    formData.append("uploaded-files", upload_detail.file);
    formData.append("site_id", siteSelected);
    formData.append("date", selectedMonthYear.endOf("M").format("YYYY-MM-DD"));
    formData.append("activity_data_id", uploadActivityDataID);

    fetchWrapper({
      endpoint_url: "emission_data_entry/uploadFile",
      set_content_type_header: false,
      body: formData,
      onSuccess: (data) => {
        if ("error" in data) {
          upload_detail.onError("File Processing Failed");
          messageApi.open({
            type: "error",
            content: data.error,
          });
        } else {
          //upload success
          upload_detail.onSuccess("File processed successfully!");
          messageApi.open({
            type: "success",
            content: data.msg,
          });
          getBackendFileList();
        }
        setUploadLoading(false);
      },
      onFetchError: (error) => {
        upload_detail.onError(`Error: ${error}`);
        setUploadLoading(false);
      },
    });
  }

  function getBackendFileList() {
    fetchWrapper({
      endpoint_url: "emission_data_entry/getBackendFileList",
      method: "POST",
      body: {
        site_ID: siteSelected,
        month_year: selectedMonthYear.endOf("M").format("YYYY-MM-DD"),
      },
      onSuccess: (response) => {
        setFileList(response.data);
      },
      onResponseError: (response) => {},
    });
  }

  function deleteFile(file) {
    const formData = new FormData();
    formData.append("activity_data_attachment_ID", file.uid);

    fetchWrapper({
      endpoint_url: "emission_data_entry/deleteFile",
      set_content_type_header: false,
      body: formData,
      onSuccess: (data) => {
        if ("error" in data) {
          messageApi.open({
            type: "error",
            content: data.error,
          });
        } else {
          //delete success
          messageApi.open({
            type: "success",
            content: data.msg,
          });
          getBackendFileList();
        }
      },
      onFetchError: (error) => {},
    });
  }

  useEffect(() => {
    if (
      siteSelected &&
      parentActiveKey.length > 0 &&
      childActiveKey.length > 0
    ) {
      if (parentActiveKey[0] === "scope_3") {
        if (
          childActiveKey[0] !== "purchased_goods_services" &&
          childActiveKey[0] !== "waste" &&
          childActiveKey[0] !== "capital_goods" &&
          childActiveKey[0] !== "fuel_energy_related_activities" &&
          childActiveKey[0] !== "upstream_leased_assets" &&
          childActiveKey[0] !== "use_sold_products" &&
          childActiveKey[0] !== "end_of_life_sold_products" &&
          childActiveKey[0] !== "downstream_leased_assets" &&
          childActiveKey[0] !== "franchises" &&
          childActiveKey[0] !== "investments" &&
          childActiveKey[0] !== "processing_sold_products"
        ) {
          if (grandchildActiveKey.length > 0) {
            getAllEmissionData();
          }
        } else {
          getAllEmissionData();
        }
      } else {
        getAllEmissionData();
      }
    }
  }, [
    siteSelected,
    parentActiveKey,
    childActiveKey,
    grandchildActiveKey,
    selectedMonthYear,
  ]);

  useEffect(() => {
    setDimensionSelection([]);

    if (parentActiveKey.length > 0 && childActiveKey.length > 0) {
      if (parentActiveKey[0] === "scope_3") {
        if (
          childActiveKey[0] !== "purchased_goods_services" &&
          childActiveKey[0] !== "waste" &&
          childActiveKey[0] !== "capital_goods" &&
          childActiveKey[0] !== "fuel_energy_related_activities" &&
          childActiveKey[0] !== "upstream_leased_assets" &&
          childActiveKey[0] !== "use_sold_products" &&
          childActiveKey[0] !== "end_of_life_sold_products" &&
          childActiveKey[0] !== "downstream_leased_assets" &&
          childActiveKey[0] !== "franchises" &&
          childActiveKey[0] !== "investments" &&
          childActiveKey[0] !== "processing_sold_products"
        ) {
          if (grandchildActiveKey.length > 0) {
            getEmissionSourceSelection();
          }
        } else {
          getEmissionSourceSelection();
        }
      } else {
        getEmissionSourceSelection();
      }
    }
  }, [parentActiveKey, childActiveKey, grandchildActiveKey]);

  useEffect(() => {
    if (siteSelected && selectedMonthYear) {
      getBackendFileList();
    }
  }, [siteSelected, selectedMonthYear]);

  return (
    <>
      <Modal
        footer={false}
        width={"90vw"}
        destroyOnClose={true}
        title="Activity Data"
        open={isActivityDataModalOpen}
        onCancel={() => setIsActivityDataModalOpen(false)}
      >
        <ActivityDataForm
          currentlyEditEmissionID={currentlyEditEmissionID}
          getAllEmissionData={getAllEmissionData}
          setIsModalOpen={setIsActivityDataModalOpen}
          existingActivityData={existingActivityData}
        />
      </Modal>

      <Modal
        footer={false}
        width={"90vw"}
        destroyOnClose={true}
        title="Factor Value"
        open={isFactorValueMarketBasedModalOpen}
        onCancel={() => setIsFactorValueMarketBasedModalOpen(false)}
      >
        <FactorValueMarketBasedSelection
          currentlyEditEmissionID={currentlyEditEmissionID}
          getAllEmissionData={getAllEmissionData}
          setIsModalOpen={setIsFactorValueMarketBasedModalOpen}
          existingFactorValueMarketBasedData={
            existingFactorValueMarketBasedData
          }
          existingFactorValueEmissionFactorIDList={
            existingFactorValueEmissionFactorIDList
          }
        />
      </Modal>

      <Modal
        footer={false}
        width={"90vw"}
        destroyOnClose={true}
        title="Activity Data Source"
        open={isActivityDataSourceModalOpen}
        onCancel={() => setIsActivityDataSourceModalOpen(false)}
      >
        <ActivityDataSource
          currentlyEditEmissionID={currentlyEditEmissionID}
          getAllEmissionData={getAllEmissionData}
          setIsModalOpen={setIsActivityDataSourceModalOpen}
          existingActivityDataSource={existingActivityDataSource}
        />
      </Modal>

      <Modal
        footer={false}
        width={"90vw"}
        destroyOnClose={true}
        title="Activity Data Remark"
        open={isActivityDataRemarkModalOpen}
        onCancel={() => setIsActivityDataRemarkModalOpen(false)}
      >
        <ActivityDataRemark
          currentlyEditEmissionID={currentlyEditEmissionID}
          getAllEmissionData={getAllEmissionData}
          setIsModalOpen={setIsActivityDataRemarkModalOpen}
          existingActivityDataRemark={existingActivityDataRemark}
        />
      </Modal>

      <Row className="pb-2">
        <Col
          md={12}
          lg={8}
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <div>Month:</div>
          <DatePicker
            picker="month"
            value={selectedMonthYear}
            style={{ width: 200, marginLeft: "8px" }}
            onChange={(value) => {
              if (value) {
                setSelectedMonthYear(value);
              }
            }}
          />
        </Col>
      </Row>
      <Collapse
        accordion
        items={scopeItems}
        onChange={(key) => {
          setParentActiveKey(key);
          setChildActiveKey([]);
        }}
        destroyInactivePanel={true}
      />
    </>
  );
}
